import type { ContextualSnapshot, ContextualSuggestion } from '../services/contextualIntelligence';
import type { Insight } from '../services/tabs';
import { derivePatternsFromTabs, type TabPatternSource } from './tabPatterns';

function insightId(prefix: string, seed: string): string {
  return `${prefix}-${seed.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 48)}`;
}

export function deriveContextualInsights(
  context: ContextualSnapshot | null,
  tabs: TabPatternSource[],
  suggestions: ContextualSuggestion[],
): Insight[] {
  const insights: Insight[] = [];
  const now = new Date().toISOString();
  const tabCount = tabs.length || context?.tabsConsidered || 0;

  if (context?.detectedIntent) {
    insights.push({
      id: insightId('intent', context.detectedIntent),
      type: 'opportunity',
      title: 'Current focus',
      description: context.detectedIntent,
      confidence: 82,
      timestamp: now,
    });
  }

  if (context?.activity && context.activity !== 'General Browsing') {
    insights.push({
      id: insightId('activity', context.activity),
      type: 'pattern',
      title: `${context.activity} activity`,
      description: `Session activity is ${context.activity.toLowerCase()} across ${tabCount} tracked tab${tabCount === 1 ? '' : 's'}.`,
      confidence: 76,
      timestamp: now,
    });
  }

  if (context && context.relatedTopics.length >= 2) {
    const topics = context.relatedTopics.slice(0, 5).join(', ');
    insights.push({
      id: 'topics-breadth',
      type: 'suggestion',
      title: 'Active research topics',
      description: `In focus: ${topics}.`,
      confidence: 74,
      timestamp: now,
    });
  }

  for (const pattern of derivePatternsFromTabs(tabs).slice(0, 5)) {
    insights.push({
      id: insightId('pattern', pattern.id),
      type: pattern.trend === 'up' ? 'warning' : 'suggestion',
      title: pattern.name,
      description: pattern.description,
      confidence: pattern.confidence,
      timestamp: now,
    });
  }

  if (tabCount >= 8) {
    insights.push({
      id: 'workspace-crowded',
      type: 'warning',
      title: 'Workspace getting crowded',
      description: `${tabCount} tabs open. Grouping or focus mode can help you stay on track.`,
      confidence: 72,
      timestamp: now,
    });
  }

  for (const suggestion of suggestions.filter((s) => s.type === 'insight').slice(0, 3)) {
    insights.push({
      id: suggestion.id,
      type: 'suggestion',
      title: suggestion.title,
      description: suggestion.description,
      confidence: suggestion.confidence,
      timestamp: now,
    });
  }

  return insights;
}

export function mergeInsights(apiInsights: Insight[], derived: Insight[]): Insight[] {
  const merged = [...apiInsights.filter((row) => !row.dismissed)];
  const seen = new Set(merged.map((row) => row.id));
  for (const row of derived) {
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    merged.push(row);
  }
  merged.sort((a, b) => b.confidence - a.confidence);
  return merged.slice(0, 12);
}

// Contextual server-driven insights response shape (loose — engine returns
// either a single summary object + patterns array or empty values).
export interface ServerInsightsResponse {
  summary: unknown;
  patterns: unknown;
  generated_at?: string;
}

// asStringArr coerces an `unknown` (e.g. from a loosely-typed JSON payload)
// into a string array. Returns [] for anything unusable — engine sometimes
// returns `null` or scalar placeholders.
function asStringArr(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  for (const item of value) {
    if (typeof item === 'string' && item.trim() !== '') {
      out.push(item);
    } else if (item && typeof item === 'object') {
      // Patterns often serialize as { name, description, confidence }.
      const obj = item as Record<string, unknown>;
      const name = typeof obj.name === 'string' ? obj.name : '';
      const desc = typeof obj.description === 'string' ? obj.description : '';
      const conf = typeof obj.confidence === 'number' ? obj.confidence : 0;
      if (name) {
        out.push(`${name}${desc ? ` — ${desc}` : ''} (${Math.round(conf)}%)`);
      }
    }
  }
  return out;
}

// mergeServerInsights combines server-side engine output with the existing
// client-derived insights and `apiInsights` rows, preferring the server when
// it has non-empty content. Falls back to `mergeInsights` when the server
// response is empty (engine still warming up, or no user activity yet).
export function mergeServerInsights(
  apiInsights: Insight[],
  derived: Insight[],
  server: ServerInsightsResponse,
): Insight[] {
  const serverRows: Insight[] = [];
  const now = new Date().toISOString();

  for (const text of asStringArr(server.summary)) {
    serverRows.push({
      id: insightId('server-summary', text),
      type: 'opportunity',
      title: 'Engine insight',
      description: text,
      confidence: 80,
      timestamp: now,
    });
  }
  for (const text of asStringArr(server.patterns)) {
    serverRows.push({
      id: insightId('server-pattern', text),
      type: 'pattern',
      title: 'Detected pattern',
      description: text,
      confidence: 78,
      timestamp: now,
    });
  }

  if (serverRows.length === 0) {
    return mergeInsights(apiInsights, derived);
  }
  const merged = [...serverRows, ...apiInsights.filter((row) => !row.dismissed)];
  const seen = new Set(merged.map((row) => row.id));
  for (const row of derived) {
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    merged.push(row);
  }
  merged.sort((a, b) => b.confidence - a.confidence);
  return merged.slice(0, 12);
}
